import { zodResolver } from "@hookform/resolvers/zod";
import React, { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeftIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getListProjectsQueryKey,
  useCreateProject,
} from "~/api/folders/folders";
import { SearchOrganizationType } from "~/api/model";
import {
  useGetOrganizationSettings,
  useImportDefaultCase,
} from "~/api/organizations/organizations";
import { searchOrganization } from "~/api/search/search";

import { getStoredUsername, ROUTES, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { cn, enableForEnv, Stages } from "~/lib/utils";
import type {
  CreateProjectBodyWithBates,
  OrganizationSettingsWithBates,
} from "~/types/bates";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import DayPicker from "../../../components/DayPicker";
import { createCaseSchema } from "../validations/create-case";
import { DuplicateCaseCard } from "./DuplicateCaseCard";
import { PipelineMode, PipelineModeSelector } from "./PipelineModeSelector";

interface CreateCaseDialogProps {
  TriggerButton: React.ReactNode;
  isFirstCase?: boolean;
  onCaseCreated?: () => void;
}

enum CASE_CREATION_STEP {
  FILL_FORM = 1,
  PIPELINE_MODE = 2,
  REVIEW = 3,
}

type CreateCaseFormData = z.infer<ReturnType<typeof createCaseSchema>>;

const DEMO_IMPORT_SCHEMA = z.object({
  caseName: z.string().optional(),
  industry: z.string().optional(),
  version: z.string().optional(),
});

type DemoImportFormData = z.infer<typeof DEMO_IMPORT_SCHEMA>;

const DEMO_IMPORT_INITIAL: DemoImportFormData = {
  caseName: "john-doe",
  industry: "fallback",
  version: "",
};

/** DEV-only demo import UI — intentionally not localized */
const DEMO_IMPORT_COPY = {
  toggleEnter: "DEV only: Import demo case",
  toggleBack: "Back to create case",
  fillFormSubtitle: "Demo case config",
  caseNameLabel: "Demo case name (optional)",
  caseNameHint:
    "If empty, the field is omitted; the API uses fallback-case-name from default-cases/config.json.",
  industryLabel: "Industry (optional)",
  industryHint: "If empty, omitted; the server typically defaults to fallback.",
  versionLabel: "Version (optional)",
  versionPlaceholder: "e.g. v2",
  versionHint:
    "Leave empty to use currentVersion from default-cases/config.json.",
  import: "Import",
  importing: "Importing…",
  successTitle: "Demo case imported",
  missingUsernameTitle: "Cannot import",
  missingUsernameDescription:
    "No stored username found. Sign out and sign in again, then retry.",
  errorTitle: "Import failed",
  errorDescription: "Could not import the demo case. Please try again.",
} as const;

const DEV_CREATE_CASE_DEMO_IMPORT_LINK_CLASS =
  "text-sm font-medium !text-amber-700 dark:!text-amber-400 hover:underline";

export const CreateCaseDialog: FC<CreateCaseDialogProps> = ({
  TriggerButton,
  isFirstCase,
  onCaseCreated,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(CASE_CREATION_STEP.FILL_FORM);

  const { organizationId } = useOrganizationParams();

  const isDevStage = enableForEnv([Stages.DEV, Stages.LOCAL]);
  const [isDemoImportMode, setIsDemoImportMode] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_SMART_AGENT");
  const [batesPrefix, setBatesPrefix] = useState("");

  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const settingsWithBates =
    organizationSettings as OrganizationSettingsWithBates;
  const isBatesEnabled = settingsWithBates?.batesNumberingEnabled === true;

  useEffect(() => {
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(
        organizationSettings.pipelineMode as PipelineMode
      );
    }
  }, [organizationSettings?.pipelineMode]);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    getValues,
    reset,
  } = useForm<CreateCaseFormData>({
    resolver: zodResolver(createCaseSchema(intl)),
    defaultValues: {
      name: "",
      claimNumber: "",
      dateOfBirth: undefined,
      dateOfLoss: undefined,
    },
    mode: "onTouched",
  });

  const {
    register: registerDemoImport,
    handleSubmit: handleSubmitDemoImport,
    reset: resetDemoImportForm,
    formState: { errors: demoImportErrors },
  } = useForm<DemoImportFormData>({
    resolver: zodResolver(DEMO_IMPORT_SCHEMA),
    defaultValues: DEMO_IMPORT_INITIAL,
    mode: "onTouched",
  });

  const { mutate, isPending } = useCreateProject();
  const { mutateAsync: importDefaultCaseAsync, isPending: isImportPending } =
    useImportDefaultCase();
  const queryClient = useQueryClient();

  const [duplicateResults, setDuplicateResults] = useState<any[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const performDuplicateSearch = async (formData: CreateCaseFormData) => {
    logger.info("Starting duplicate search with data:", formData);
    setIsSearchLoading(true);
    try {
      const searchParams: any = {
        type: SearchOrganizationType.cases,
        pageSize: 10,
        page: 1,
        query: formData.name || "",
      };

      if (formData.name) {
        searchParams.name = formData.name;
      }
      if (formData.dateOfBirth) {
        searchParams.dateOfBirth = dayjs(formData.dateOfBirth).format(
          "YYYY-MM-DD"
        );
      }
      if (formData.claimNumber) {
        searchParams.claimNumber = formData.claimNumber;
      }

      logger.info("Search params:", searchParams);
      const response = await searchOrganization(organizationId, searchParams);
      logger.info("Search response:", response);
      const results = response.cases?.slice(0, 4) || [];
      setDuplicateResults(results);
      return results;
    } catch (error) {
      logger.error("Error searching for duplicates:", error);
      setDuplicateResults([]);
      return [];
    } finally {
      setIsSearchLoading(false);
    }
  };

  const createCase = (data: CreateCaseFormData) => {
    const body: CreateProjectBodyWithBates = {
      name: data.name,
      claimNumber: data.claimNumber || "",
      dateOfBirth: data.dateOfBirth
        ? dayjs(data.dateOfBirth).format("YYYY-MM-DD")
        : "",
      dateOfLoss: data.dateOfLoss
        ? dayjs(data.dateOfLoss).format("YYYY-MM-DD")
        : undefined,
      pipelineMode: selectedPipelineMode,
      ...(isBatesEnabled && batesPrefix ? { batesPrefix } : {}),
    };

    trackEvent({
      eventName: "Case_Created",
      properties: {
        pipelineMode: selectedPipelineMode,
        source: "CreateCaseDialog",
        isFirstCase: isFirstCase,
      },
    });

    mutate(
      {
        organizationId,
        data: body,
      },
      {
        onSuccess: (createdCase) => {
          setIsOpen(false);
          setCurrentStep(CASE_CREATION_STEP.FILL_FORM);
          queryClient.invalidateQueries({
            queryKey: getListProjectsQueryKey(organizationId),
          });
          onCaseCreated?.();
          navigate(ROUTES.CASE({ organizationId, caseId: createdCase.id }));
        },
      }
    );
  };

  const runDemoImport = async (data: DemoImportFormData) => {
    const username = getStoredUsername();
    if (!username) {
      toast({
        title: DEMO_IMPORT_COPY.missingUsernameTitle,
        description: DEMO_IMPORT_COPY.missingUsernameDescription,
        variant: "destructive",
      });
      return;
    }

    trackEvent({ eventName: "app/create_case_modal/demo_import/submit" });

    try {
      const trimmedCaseName = data.caseName?.trim() ?? "";
      const trimmedIndustry = data.industry?.trim() ?? "";
      const trimmedVersion = data.version?.trim() ?? "";

      const response = await importDefaultCaseAsync({
        organizationId,
        data: {
          username,
          ...(trimmedCaseName ? { caseName: trimmedCaseName } : {}),
          ...(trimmedIndustry ? { industry: trimmedIndustry } : {}),
          ...(trimmedVersion ? { version: trimmedVersion } : {}),
        },
      });

      toast({
        title: DEMO_IMPORT_COPY.successTitle,
        description: `Resolved: ${response.resolvedCaseName} / ${response.resolvedIndustry} / ${response.resolvedVersion}`,
      });

      setIsOpen(false);
      setCurrentStep(CASE_CREATION_STEP.FILL_FORM);
      setIsDemoImportMode(false);
      resetDemoImportForm(DEMO_IMPORT_INITIAL);
      reset({
        name: "",
        claimNumber: "",
        dateOfBirth: undefined,
        dateOfLoss: undefined,
      });
      queryClient.invalidateQueries({
        queryKey: getListProjectsQueryKey(organizationId),
      });
      onCaseCreated?.();
      navigate(
        ROUTES.CASE({
          organizationId,
          caseId: response.newFolderId,
        })
      );
    } catch {
      toast({
        title: DEMO_IMPORT_COPY.errorTitle,
        description: DEMO_IMPORT_COPY.errorDescription,
        variant: "destructive",
      });
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (currentStep === CASE_CREATION_STEP.FILL_FORM) {
      if (isFirstCase) {
        createCase(data);
      } else {
        setCurrentStep(CASE_CREATION_STEP.PIPELINE_MODE);
      }
    } else if (currentStep === CASE_CREATION_STEP.PIPELINE_MODE) {
      const duplicateResults = await performDuplicateSearch(data);

      if (duplicateResults && duplicateResults.length > 0) {
        setCurrentStep(CASE_CREATION_STEP.REVIEW);
      } else {
        createCase(data);
      }
    } else {
      createCase(data);
    }
  });

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (currentStep === CASE_CREATION_STEP.FILL_FORM && isDemoImportMode) {
      e.preventDefault();
      void handleSubmitDemoImport(runDemoImport)(e);
      return;
    }
    void onSubmit(e);
  };

  const handleBack = () => {
    if (currentStep === CASE_CREATION_STEP.REVIEW) {
      setCurrentStep(CASE_CREATION_STEP.PIPELINE_MODE);
    } else {
      setCurrentStep(CASE_CREATION_STEP.FILL_FORM);
    }
  };

  const resetForm = () => {
    reset({
      name: "",
      claimNumber: "",
      dateOfBirth: undefined,
      dateOfLoss: undefined,
    });
    resetDemoImportForm(DEMO_IMPORT_INITIAL);
    setIsDemoImportMode(false);
    setBatesPrefix("");
    setCurrentStep(CASE_CREATION_STEP.FILL_FORM);
    setDuplicateResults([]);
    setIsSearchLoading(false);
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(
        organizationSettings.pipelineMode as PipelineMode
      );
    } else {
      setSelectedPipelineMode("USE_SMART_AGENT");
    }
  };

  const handleCancel = () => {
    resetForm();
    setIsOpen(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      <DialogContent className="sm:max-w-[608px]">
        <DialogHeader className="space-y-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <DialogTitle>
              <FormattedMessage
                id="casesList.createCaseDialog.title"
                defaultMessage={"Create Case"}
              />
            </DialogTitle>
            {isDevStage && (
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  "h-auto shrink-0 self-end py-1 px-2",
                  DEV_CREATE_CASE_DEMO_IMPORT_LINK_CLASS
                )}
                onClick={() => {
                  trackEvent({
                    eventName: isDemoImportMode
                      ? "app/create_case_modal/demo_import/back_to_create/click"
                      : "app/create_case_modal/demo_import/enter/click",
                  });
                  setIsDemoImportMode((prev) => {
                    if (prev) {
                      resetDemoImportForm(DEMO_IMPORT_INITIAL);
                    }
                    return !prev;
                  });
                }}
              >
                {isDemoImportMode
                  ? DEMO_IMPORT_COPY.toggleBack
                  : DEMO_IMPORT_COPY.toggleEnter}
              </Button>
            )}
          </div>
          <FormSubtitle
            currentStep={currentStep}
            intl={intl}
            isDemoImportMode={isDemoImportMode}
          />
        </DialogHeader>
        <form onSubmit={handleFormSubmit}>
          {currentStep === CASE_CREATION_STEP.FILL_FORM && isDemoImportMode && (
            <>
              <div className="flex flex-col gap-4 mb-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="demo-import-case-name">
                    {DEMO_IMPORT_COPY.caseNameLabel}
                  </Label>
                  <Input
                    id="demo-import-case-name"
                    autoComplete="off"
                    disabled={isImportPending}
                    className="shadow-none"
                    {...registerDemoImport("caseName")}
                  />
                  {demoImportErrors.caseName && (
                    <span className="text-red-500 text-sm">
                      {demoImportErrors.caseName.message}
                    </span>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {DEMO_IMPORT_COPY.caseNameHint}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="demo-import-industry">
                    {DEMO_IMPORT_COPY.industryLabel}
                  </Label>
                  <Input
                    id="demo-import-industry"
                    autoComplete="off"
                    disabled={isImportPending}
                    className="shadow-none"
                    {...registerDemoImport("industry")}
                  />
                  {demoImportErrors.industry && (
                    <span className="text-red-500 text-sm">
                      {demoImportErrors.industry.message}
                    </span>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {DEMO_IMPORT_COPY.industryHint}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="demo-import-version">
                    {DEMO_IMPORT_COPY.versionLabel}
                  </Label>
                  <Input
                    id="demo-import-version"
                    autoComplete="off"
                    disabled={isImportPending}
                    className="shadow-none"
                    placeholder={DEMO_IMPORT_COPY.versionPlaceholder}
                    {...registerDemoImport("version")}
                  />
                  {demoImportErrors.version && (
                    <span className="text-red-500 text-sm">
                      {demoImportErrors.version.message}
                    </span>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {DEMO_IMPORT_COPY.versionHint}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isImportPending}
                  onClick={() => {
                    trackEvent({
                      eventName:
                        "app/create_case_modal/demo_import/cancel.button/click",
                    });
                    handleCancel();
                  }}
                >
                  {intl.formatMessage({
                    id: "cancel",
                    defaultMessage: "Cancel",
                  })}
                </Button>
                <Button
                  disabled={isImportPending}
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isImportPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {DEMO_IMPORT_COPY.importing}
                    </>
                  ) : (
                    DEMO_IMPORT_COPY.import
                  )}
                </Button>
              </div>
            </>
          )}
          {currentStep === CASE_CREATION_STEP.FILL_FORM &&
            !isDemoImportMode && (
              <>
                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="caseName">
                      <FormattedMessage id="casesList.createCaseDialog.caseNameLabel" />
                    </Label>
                    <Input
                      id="caseName"
                      placeholder={intl.formatMessage({
                        id: "casesList.createCaseDialog.caseNamePlaceholder",
                      })}
                      className="shadow-none"
                      {...register("name")}
                    />
                    {errors.name && (
                      <span className="text-red-500 text-sm">
                        {errors.name.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="dateOfBirth">
                      <FormattedMessage
                        id="dateOfBirthLabelOptional"
                        defaultMessage="Date of birth (optional)"
                      />
                    </Label>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <DayPicker
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date: Date | null) => {
                              field.onChange(date);
                            }}
                            placeholder="MM/DD/YYYY"
                            minDate={new Date("1900-01-01")}
                            maxDate={new Date()}
                          />
                        </div>
                      )}
                    />
                    {errors.dateOfBirth && (
                      <span className="text-red-500 text-sm">
                        {errors.dateOfBirth.message}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="dateOfLoss">
                      <FormattedMessage
                        id="dateOfLossLabelOptional"
                        defaultMessage="Date of loss (optional)"
                      />
                    </Label>
                    <Controller
                      name="dateOfLoss"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <DayPicker
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date: Date | null) => {
                              field.onChange(date);
                            }}
                            placeholder="MM/DD/YYYY"
                            minDate={new Date("1900-01-01")}
                            maxDate={new Date()}
                          />
                        </div>
                      )}
                    />
                    {errors.dateOfLoss && (
                      <span className="text-red-500 text-sm">
                        {errors.dateOfLoss.message}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="claimNumber">
                      <FormattedMessage
                        id="claimNumberLabelOptional"
                        defaultMessage="Claim/Case # (optional)"
                      />
                    </Label>
                    <Input
                      id="claimNumber"
                      placeholder="235848593"
                      className="shadow-none"
                      {...register("claimNumber")}
                    />
                    {errors.claimNumber && (
                      <span className="text-red-500 text-sm">
                        {errors.claimNumber.message}
                      </span>
                    )}
                  </div>
                  {isBatesEnabled && (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="batesPrefix">
                        <FormattedMessage
                          id="casesList.createCaseDialog.batesPrefixLabel"
                          defaultMessage="Bates Prefix (Optional)"
                        />
                      </Label>
                      <Input
                        id="batesPrefix"
                        placeholder={intl.formatMessage({
                          id: "casesList.createCaseDialog.batesPrefixPlaceholder",
                          defaultMessage: "e.g. SMITH, DOE, PLT",
                        })}
                        className="shadow-none"
                        value={batesPrefix}
                        onChange={(e) => setBatesPrefix(e.target.value)}
                      />
                      <span className="text-sm text-muted-foreground">
                        <FormattedMessage
                          id="casesList.createCaseDialog.batesPrefixHint"
                          defaultMessage="If left blank, a prefix will be auto-generated from the case name."
                        />
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      trackEvent({
                        eventName:
                          "app/create_case_modal/step_1/cancel.button/click",
                      });
                      handleCancel();
                    }}
                  >
                    {intl.formatMessage({
                      id: "cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    disabled={isPending || !isValid}
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      trackEvent({
                        eventName:
                          "app/create_case_modal_step_1/continue.button/submit",
                      });
                    }}
                  >
                    {isFirstCase ? (
                      <FormattedMessage
                        id="casesList.createCaseDialog.createCase"
                        defaultMessage={"Create Case"}
                      />
                    ) : (
                      <FormattedMessage
                        id="casesList.createCaseDialog.continue"
                        defaultMessage={"Continue"}
                      />
                    )}
                  </Button>
                </div>
              </>
            )}
          {currentStep === CASE_CREATION_STEP.PIPELINE_MODE && (
            <>
              <div className="flex flex-col gap-4 mb-4">
                <PipelineModeSelector
                  selectedMode={selectedPipelineMode}
                  onModeChange={setSelectedPipelineMode}
                />
              </div>
              <div className="flex justify-between">
                <Button type="button" variant="secondary" onClick={handleBack}>
                  <ChevronLeftIcon className="w-4 h-4 mr-2" />
                  {intl.formatMessage({
                    id: "casesList.createCaseDialog.editInformation",
                    defaultMessage: "Edit",
                  })}
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      trackEvent({
                        eventName:
                          "app/create_case_modal/step_2/cancel.button/click",
                      });
                      handleCancel();
                    }}
                  >
                    {intl.formatMessage({
                      id: "cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    disabled={isPending || !isValid || isSearchLoading}
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      trackEvent({
                        eventName:
                          "app/create_case_modal/step_2/continue.button/submit",
                        properties: {
                          pipelineMode: selectedPipelineMode,
                        },
                      });
                    }}
                  >
                    {isSearchLoading ? (
                      <FormattedMessage
                        id="casesList.createCaseDialog.searching"
                        defaultMessage={"Searching..."}
                      />
                    ) : (
                      <FormattedMessage
                        id="casesList.createCaseDialog.continue"
                        defaultMessage={"Continue"}
                      />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
          {currentStep === CASE_CREATION_STEP.REVIEW && (
            <div>
              <div>
                {getValues("name") && (
                  <ReviewField
                    label={intl.formatMessage({
                      id: "casesList.createCaseDialog.caseNameLabel",
                    })}
                    value={getValues("name")}
                  />
                )}
                {getValues("dateOfBirth") && (
                  <ReviewField
                    label={intl.formatMessage({
                      id: "dateOfBirthLabel",
                    })}
                    value={formatDateForDisplay(getValues("dateOfBirth"))}
                  />
                )}
                {getValues("dateOfLoss") && (
                  <ReviewField
                    label={intl.formatMessage({
                      id: "dateOfLossLabel",
                    })}
                    value={formatDateForDisplay(getValues("dateOfLoss"))}
                  />
                )}
                {getValues("claimNumber") && (
                  <ReviewField
                    label={intl.formatMessage({
                      id: "claimNumberLabel",
                    })}
                    value={getValues("claimNumber")}
                    isLast={true}
                  />
                )}
              </div>

              {(isSearchLoading || duplicateResults.length > 0) && (
                <div className="mt-6 mb-4">
                  <h3 className="font-medium text-sm">
                    <FormattedMessage
                      id="casesList.createCaseDialog.possibleDuplicates"
                      defaultMessage="Possible Duplicates"
                    />
                  </h3>
                  <div className="max-h-[40vh] overflow-y-auto mt-2">
                    {isSearchLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {duplicateResults.map((duplicate: any) => (
                          <DuplicateCaseCard
                            key={duplicate.id}
                            caseData={duplicate}
                            onViewCase={() =>
                              navigate(
                                ROUTES.CASE({
                                  organizationId,
                                  caseId: duplicate.id,
                                })
                              )
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-between mt-4">
                <Button type="button" variant="secondary" onClick={handleBack}>
                  <ChevronLeftIcon className="w-4 h-4 mr-2" />
                  {intl.formatMessage({
                    id: "casesList.createCaseDialog.editInformation",
                    defaultMessage: "Edit",
                  })}
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                  >
                    {intl.formatMessage({
                      id: "cancel",
                      defaultMessage: "Cancel",
                    })}
                  </Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={isPending || !isValid}
                    type="submit"
                  >
                    {intl.formatMessage({
                      id: "createCase",
                      defaultMessage: "Create Case",
                    })}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const formatDateForDisplay = (date: Date | undefined): string | undefined => {
  if (!date) return;
  return dayjs(date).format("MM/DD/YYYY");
};

export const FormSubtitle = ({
  currentStep,
  intl,
  isDemoImportMode = false,
}: {
  currentStep: CASE_CREATION_STEP;
  intl: IntlShape;
  isDemoImportMode?: boolean;
}) => {
  return (
    <>
      {currentStep === CASE_CREATION_STEP.FILL_FORM && (
        <div>
          <span className="text-zinc-500 text-sm font-semibold leading-tight">
            {intl.formatMessage({
              id: "casesList.createCaseDialog.step1",
            })}
          </span>
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            :{" "}
            {isDemoImportMode
              ? DEMO_IMPORT_COPY.fillFormSubtitle
              : intl.formatMessage({
                  id: "casesList.createCaseDialog.fillForm",
                })}
          </span>
        </div>
      )}
      {currentStep === CASE_CREATION_STEP.PIPELINE_MODE && (
        <div>
          <span className="text-zinc-500 text-sm font-semibold leading-tight">
            {intl.formatMessage({
              id: "casesList.createCaseDialog.step2",
            })}
          </span>
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            :{" "}
            {intl.formatMessage({
              id: "casesList.createCaseDialog.selectPipelineMode",
            })}
          </span>
        </div>
      )}
      {currentStep === CASE_CREATION_STEP.REVIEW && (
        <div>
          <span className="text-zinc-500 text-sm font-semibold leading-tight">
            {intl.formatMessage({
              id: "casesList.createCaseDialog.step3",
            })}
          </span>
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            :{" "}
            {intl.formatMessage({
              id: "casesList.createCaseDialog.reviewInfo",
            })}
          </span>
        </div>
      )}
    </>
  );
};

const ReviewField = ({
  label,
  value,
  isLast = false,
}: {
  label: string;
  value?: string;
  isLast?: boolean;
}) => {
  return (
    <div
      className={cn(
        "w-full py-3 border-b border-border flex justify-between items-start gap-4",
        isLast && "border-b-0"
      )}
    >
      <div className="text-muted-foreground text-sm font-normal leading-tight min-w-[100px]">
        {label}:
      </div>
      <div className="text-foreground text-sm font-normal leading-tight text-right break-words truncate max-w-[390px]">
        {value ?? "-"}
      </div>
    </div>
  );
};
